import React from 'react';
import { Link } from 'react-router-dom';
import Form from '../common/Form';
import FormSection from '../common/FormSection';
import FormField from '../common/FormField';
import SimpleAlert from '../common/SimpleAlert';
import ImageLink from '../common/ImageLink';
import FileUpload from '../common/FileUpload';
import ExerciseTemplateScoring from '../exerciseTemplate/ExerciseTemplateScoring';
import AuditLogTable from '../tables/AuditLogTable';
import handleChange from '../../utils/formUtils/handleChange';
import { getRoles } from '../../utils/tokenUtils';
import { confirmAction } from '../../utils/sweetAlertUtils';
import {
  ScoreDTO,
  CourseDTO,
  FirearmSpecificDTO,
  MemberFirearmDTO,
} from '../../services/apiClient';
import { useScoreFormLogic } from '../../hooks/useScoreFormLogic';
import { ScoreTypeConstants } from '../../constants/ScoreTypeConstants';
import { FilteredExerciseData } from '../../utils/filterExerciseTypes';
import FeedbackForm from './FeedbackForm';
import { ComponentConstants } from '../../constants/ComponentConstants';
import MemberDetails from '../common/MemberDetails';
import { FileTypeConstants } from '../../constants/FileTypeConstants';
import { buildFirearmOptions } from '../../utils/formUtils/buildFirearmOptions';

interface BaseScoreFormProps {
  initialValues: ScoreDTO;
  onSubmit: (data: ScoreDTO) => void;
  onCancel: () => void;
  isLoading?: boolean;
  course?: CourseDTO;
  exerciseTypeFilter?: (
    firearmSpecific: FirearmSpecificDTO,
    course: CourseDTO | undefined,
    selectedFirearm: MemberFirearmDTO,
  ) => FilteredExerciseData;
  scoreTypeConstant?: string;
  eventTypeMessage?: string;
}

const BaseScoreForm: React.FC<BaseScoreFormProps> = ({
  initialValues,
  onSubmit,
  onCancel,
  isLoading = false,
  course,
  exerciseTypeFilter,
  scoreTypeConstant = ScoreTypeConstants.IN_HOUSE,
  eventTypeMessage,
}) => {
  const roles = getRoles();
  const statusFieldName = 'scoreStatusId';

  const {
    formData,
    setFormData,
    member,
    memberFirearms,
    exerciseTypes,
    activityTypes,
    scoreStatuses,
    exerciseTemplate,
    isLoadingTemplate,
    exerciseSpecifics,
    auditLogs,
    auditCurrentPage,
    setAuditCurrentPage,
    auditTotalPages,
    auditPageSize,
    setAuditPageSize,
    isLoadingMember,
    isLoadingMemberFirearms,
    isLoadingActivityTypes,
    isLoadingScoreStatuses,
    isFirearmSpecific,
    isLoadingAuditLogs,
    errors,
    validate,
    handleFileChange,
    getFileIdForTarget,
    handleScoreDataChange,
    isActivityTypeDisabled,
    isLockedExerciseSelection,
    uploadingFiles,
  } = useScoreFormLogic({
    initialValues,
    course,
    exerciseTypeFilter,
    scoreTypeConstant,
  });

  const FORM_SECTIONS = {
    ...(roles?.includes('Admin')
      ? {
        memberDetails: { id: 'member-details', title: 'Member Details' },
        recordDetails: { id: 'record-details', title: 'Record Details' },
        documents: { id: 'documents', title: 'Documents' },
        scores: { id: 'scores', title: 'Scores' },
        feedback: { id: 'feedback', title: 'Feedback' },
        notes: { id: 'notes', title: 'Notes' },
        history: { id: 'history', title: 'History' },
      }
      : {}),
    details: { id: 'details', title: 'Details' },
    uploadDocuments: { id: 'upload-documents', title: 'Upload Documents' },
    completeScores: { id: 'complete-scores', title: 'Complete Scores' },
  } as const;

  const handleSave = async () => {
    // Validate first before showing confirmation
    if (!validate(formData)) {
      return;
    }

    // For regular users, show confirmation dialog after validation
    if (roles?.includes('User')) {
      const confirmed = await confirmAction({
        title: 'Confirm Score Details',
        text: `I, ${member?.firstName} ${member?.lastName} (${member?.idPassportNumber}), hereby confirm that the score entered is correct and was shot by myself on the date: ${formData.dateActivity ?? ''} at location: ${formData.location ?? ''}.`,
        icon: 'warning',
        confirmButtonText: 'Yes, I confirm',
        cancelButtonText: 'Cancel',
      });

      if (!confirmed) return;
    }

    // Proceed with submission
    onSubmit(formData);
  };

  const sections = Object.values(FORM_SECTIONS);

  return (
    <Form sections={sections} onSave={handleSave} onCancel={onCancel} isSubmitting={isLoading}>
      {roles?.includes('Admin') && FORM_SECTIONS.memberDetails && (
        <FormSection id={FORM_SECTIONS.memberDetails.id} title={FORM_SECTIONS.memberDetails.title}>
          <MemberDetails member={formData.member ?? {}} />
        </FormSection>
      )}

      <FormSection
        id={
          roles?.includes('Admin') && FORM_SECTIONS.recordDetails
            ? FORM_SECTIONS.recordDetails.id
            : FORM_SECTIONS.details.id
        }
        title={
          roles?.includes('Admin') && FORM_SECTIONS.recordDetails
            ? FORM_SECTIONS.recordDetails.title
            : FORM_SECTIONS.details.title
        }
      >
        {eventTypeMessage && (
          <SimpleAlert variant="warning" message={eventTypeMessage} />
        )}

        {memberFirearms.length === 0 ? (
            <SimpleAlert
              variant="warning"
              message={
                <>
                  You have no active or completed firearms on record, please{' '}
                  <Link to="/admin/memberfirearms/create" className="text-blue-600 dark:text-blue-400 hover:underline font-semibold">
                    Add a firearm
                  </Link>{' '}
                  or complete your{' '}
                  <Link to="/admin/memberfirearms" className="text-blue-600 dark:text-blue-400 hover:underline font-semibold">
                    firearm records
                  </Link>{' '}
                  first before you continue!
                </>
              }
            />
        ) : (
          scoreTypeConstant === ScoreTypeConstants.COURSE_PRACTICAL_EVALUATION && (
              <SimpleAlert
                variant="warning"
                message={
                  <>
                    You can only select certain firearms and exercises to do your Dedicated Course Practical Evaluations.
                    If no exercises displays once you select a firearm please select another firearm.
                  </>
                }
              />
          )
        )}

        <FormField
          id="memberFirearmId"
          label="Member Firearm"
          type="dropdown"
          value={formData.memberFirearmId || ''}
          onChange={(e) => handleChange(e, setFormData)}
          placeholder="Select Member Firearm"
          required={true}
          error={errors.memberFirearmId}
          options={buildFirearmOptions(memberFirearms)}
          isLoading={isLoadingMemberFirearms}
          helper={
            <>
              Only complete and active firearms will display in the list.
              <br />
              Not on list, <Link to="/admin/memberfirearms" className="text-blue-600 dark:text-blue-400 hover:underline">manage</Link> or <Link to="/admin/memberfirearms/create" className="text-blue-600 dark:text-blue-400 hover:underline">add a new firearm</Link>?
            </>
          }
        />

        <FormField
          id="location"
          label="Shooting range name"
          type="text"
          value={formData.location || ''}
          onChange={(e) => handleChange(e, setFormData)}
          placeholder="Enter Location"
          required={true}
          error={errors.location}
        />

        <FormField
          id="dateActivity"
          label="Date target shot"
          type="date"
          value={formData.dateActivity || ''}
          onChange={(e) => handleChange(e, setFormData)}
          placeholder="Enter Date Activity"
          required={true}
          error={errors.dateActivity}
          allowFutureDates={false}
        />

        <FormField
          id="exerciseTypeId"
          label="Exercise Type"
          type="dropdown"
          value={formData.exerciseTypeId || ''}
          onChange={(e) => handleChange(e, setFormData)}
          placeholder="Select Exercise Type"
          required={true}
          error={errors.exerciseTypeId}
          options={exerciseTypes.map((type) => ({
            value: type.id,
            label: type.title || '',
          }))}
          isLoading={isFirearmSpecific}
          disabled={isLockedExerciseSelection}
          helper={isLockedExerciseSelection ? 'Determined by the scanned target' : undefined}
        />

        <FormField
          id="exerciseSpecificId"
          label="Specific Shooting Exercise"
          type="dropdown"
          value={formData.exerciseSpecificId || ''}
          onChange={(e) => handleChange(e, setFormData)}
          placeholder="Select Specific Exercise"
          required={true}
          error={errors.exerciseSpecificId}
          options={exerciseSpecifics.map((specific) => ({
            value: specific.id,
            label: specific.title || '',
          }))}
          isLoading={isFirearmSpecific}
          disabled={isLockedExerciseSelection}
          helper={isLockedExerciseSelection ? 'Determined by the scanned target' : undefined}
        />

        <FormField
          id="activityTypeId"
          label="Activity Type"
          type="dropdown"
          value={formData.activityTypeId || ''}
          onChange={(e) => handleChange(e, setFormData)}
          placeholder="Select Activity Type"
          required={true}
          error={errors.activityTypeId}
          options={activityTypes.map((type) => ({
            value: type.id,
            label: type.title || '',
          }))}
          isLoading={isLoadingActivityTypes}
          disabled={isActivityTypeDisabled}
          helper="Select an activity type that the postal shoot activity will count towards"
        />

        {roles?.includes('User') && (
          <FormField
            id="notes"
            label="Notes(Optional)"
            type="textarea"
            value={formData.notes || ''}
            onChange={(e) => handleChange(e, setFormData)}
            placeholder="Enter Notes"
            required={false}
            error={errors.notes}
          />
        )}
      </FormSection>

      <FormSection
        id={
          roles?.includes('Admin') && FORM_SECTIONS.documents
            ? FORM_SECTIONS.documents.id
            : FORM_SECTIONS.uploadDocuments.id
        }
        title={
          roles?.includes('Admin') && FORM_SECTIONS.documents
            ? FORM_SECTIONS.documents.title
            : FORM_SECTIONS.uploadDocuments.title
        }
      >
        {roles?.includes('User') && (
          <SimpleAlert
            variant="warning"
            message={
              formData.exerciseSpecific?.id
                ? 'Please match the document number (in the top right corner of your target/score sheet) with the relevant field below.'
                : 'Remember to scan your shot target in PDF format and then to attach them at the browse button below. Please select a Shooting Exercise to continue. For any questions or topics relating to Scores use the help icon at the top right corner.'
            }
          />
        )}

        {(formData.id || formData.exerciseSpecific?.id) && (
          <>
            {formData.exerciseSpecific?.exerciseSpecificTargets?.map(
              (target: any, index: number) => {
                const fileId = getFileIdForTarget(target.exerciseTargetId);

                return (
                  <div key={target.exerciseTargetId} className='col-span-full'>
                    {fileId && (
                      <ImageLink
                        fileId={fileId}
                        label={`${target.exerciseTarget?.title || 'Target'} ${index + 1}`}
                      />
                    )}
                    <FileUpload
                      id={`target-${target.exerciseTargetId}`}
                      onChange={handleFileChange(target.exerciseTargetId)}
                      title={`${target.exerciseTarget?.title || 'Target'} ${index + 1} - ${target.exerciseTarget?.code || ''}`}
                      accept={FileTypeConstants.ALL_SUPPORTED}
                      required={true}
                      error={errors[`target-${target.exerciseTargetId}`]}
                      isUploading={uploadingFiles[`target-${target.exerciseTargetId}`]}
                    />
                  </div>
                );
              },
            )}

            {formData.fileAdditionalId && (
              <ImageLink fileId={formData.fileAdditionalId} label="Additional File" />
            )}
            <FileUpload
              id="fileAdditionalId"
              onChange={handleFileChange()}
              title="Additional Documents (optional)"
              accept={FileTypeConstants.ALL_SUPPORTED}
              required={false}
              error={errors.fileAdditionalId}
              isUploading={uploadingFiles['fileAdditionalId']}
            />
          </>
        )}
      </FormSection>

      <FormSection
        id={
          roles?.includes('Admin') && FORM_SECTIONS.scores
            ? FORM_SECTIONS.scores.id
            : FORM_SECTIONS.completeScores?.id
        }
        title={
          roles?.includes('Admin') && FORM_SECTIONS.scores
            ? FORM_SECTIONS.scores.title
            : FORM_SECTIONS.completeScores?.title
        }
      >
        {!formData.exerciseSpecific?.id && (
          <SimpleAlert variant="warning" message="Please select a Shooting Exercise to continue." />
        )}

        {formData.exerciseSpecific?.id && !exerciseTemplate && !isLoadingTemplate && (
          <SimpleAlert
            variant="warning"
            message="No exercise template found for the selected exercise."
          />
        )}

        {isLoadingTemplate && <SimpleAlert variant="info" message="Loading exercise template..." />}

        {formData.exerciseSpecific?.id && exerciseTemplate && !isLoadingTemplate && (
          <div className="mb-6 col-span-full">
            <ExerciseTemplateScoring
              template={exerciseTemplate}
              existingScoreSections={formData.scoreSections}
              onScoreDataChange={handleScoreDataChange}
              error={errors.scoreSections}
            />
          </div>
        )}
      </FormSection>

      {roles?.includes('Admin') && FORM_SECTIONS.feedback && (
        <FormSection id={FORM_SECTIONS.feedback.id} title={FORM_SECTIONS.feedback.title}>
          <FeedbackForm
            componentId={ComponentConstants.SCORES}
            recordId={formData.id ?? ''}
            initialData={formData}
            componentStatuses={scoreStatuses.map((status) => ({
              value: status.id,
              label: status.title,
            }))}
            statusFieldName={statusFieldName}
            onChange={(updatedData) => {
              setFormData((prev) => ({
                ...prev,
                ...updatedData,
              }));
            }}
          />
        </FormSection>
      )}

      {roles?.includes('Admin') && FORM_SECTIONS.notes && (
        <FormSection id={FORM_SECTIONS.notes.id} title={FORM_SECTIONS.notes.title} columns={1}>
          <FormField
            id="notes"
            label="Notes(Optional)"
            type="textarea"
            value={formData.notes || ''}
            onChange={(e) => handleChange(e, setFormData)}
            placeholder="Enter Notes"
            required={false}
            error={errors.notes}
          />
        </FormSection>
      )}

      {roles?.includes('Admin') && FORM_SECTIONS.history && (
        <FormSection id={FORM_SECTIONS.history.id} title={FORM_SECTIONS.history.title}>
          <AuditLogTable
            data={auditLogs}
            currentPage={auditCurrentPage}
            totalPages={auditTotalPages}
            setCurrentPage={setAuditCurrentPage}
            pageSize={auditPageSize}
            setPageSize={setAuditPageSize}
            isLoading={isLoadingAuditLogs}
          />
        </FormSection>
      )}
    </Form>
  );
};

export default BaseScoreForm;
